import { type ReactNode, useEffect, useRef, useState } from "react";
import CloudImportPanel, { type CloudImportActions } from "./CloudImportPanel";
import { i18n, useTranslation } from "./i18n";
import {
  BackupFolderError,
  saveBackupToDirectory,
  selectBackupDirectory,
  supportsBackupDirectory,
} from "./lib/backupFolder";
import {
  type BackupStage,
  createEncryptedBackup,
  decryptBackupInWorker,
  storageCapacityNotice,
} from "./lib/localStore/backupJob";
import { type getLocalStorageStatus, requestPersistentStorage } from "./lib/localStore/client";
import type { LocalBackupSource, LocalSnapshot } from "./lib/localStore/repository";
import ToolPageHeader from "./ToolPageHeader";

function backupDeviceId(): string {
  const storageKey = "classroom-backup-device-id";
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored?.trim() && stored.length <= 128) return stored;
    const id = crypto.randomUUID();
    localStorage.setItem(storageKey, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

type StorageStatus = Awaited<ReturnType<typeof getLocalStorageStatus>>;
type BackupOperationState =
  | "idle"
  | "exporting"
  | "downloaded"
  | "folder-writing"
  | "folder-saved"
  | "restoring"
  | "restored"
  | "canceled"
  | "error";

export default function LocalDataPanel({
  backupControl,
  sessionStatus,
  storageStatus: status,
  onStorageStatusChange,
  onExport,
  onRestore,
  onPreviewCloudImport,
  onImportCloudExport,
  hideCloudImport = false,
}: CloudImportActions & {
  backupControl?: ReactNode;
  sessionStatus?: ReactNode;
  storageStatus?: StorageStatus;
  onStorageStatusChange: (status: StorageStatus) => void;

  hideCloudImport?: boolean;
  onExport: () => Promise<LocalBackupSource>;
  onRestore: (snapshot: LocalSnapshot, sqliteImage?: string, schemaVersion?: number) => Promise<void>;
}) {
  const { t } = useTranslation("keyboard");
  const { t: folderText } = useTranslation("backupFolder");
  const { t: copy } = useTranslation("localData");
  const { t: brandText } = useTranslation("brand");
  const { t: yearText } = useTranslation("schoolYears");
  const [backupState, setBackupState] = useState<BackupOperationState>("idle");
  const [persistencePending, setPersistencePending] = useState(false);
  const [persistenceMessage, setPersistenceMessage] = useState("");
  const [persistenceError, setPersistenceError] = useState("");
  const [password, setPassword] = useState("");
  const [file, setFile] = useState<File>();
  const [busy, setBusy] = useState(false);
  const [backupStage, setBackupStage] = useState<BackupStage>();
  const backupController = useRef<AbortController | undefined>(undefined);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    return () => backupController.current?.abort();
  }, []);

  async function downloadBackup(destination: "download" | "folder" = "download") {
    if (busy) return;
    try {
      setBusy(true);
      setBackupState("exporting");
      setError("");
      setMessage("");
      const controller = new AbortController();
      backupController.current = controller;
      const directory = destination === "folder" ? await selectBackupDirectory() : undefined;
      controller.signal.throwIfAborted();
      const encrypted = await createEncryptedBackup(
        onExport,
        password,
        backupDeviceId(),
        controller.signal,
        setBackupStage,
      );
      controller.signal.throwIfAborted();
      if (directory) {
        setBackupState("folder-writing");
        const filename = await saveBackupToDirectory(directory, encrypted, controller.signal);
        setBackupState("folder-saved");
        setMessage(folderText("saved", { folder: directory.name, filename }));
        return;
      }
      const blob = new Blob([encrypted], { type: "application/octet-stream" });
      if (blob.size > 100 * 1024 * 1024) throw new Error(i18n.t("quickBackup:tooLarge"));
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${brandText("backupFilenamePrefix")}-${new Date().toISOString().slice(0, 10)}.rollclassbackup`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setBackupState("downloaded");
      setMessage(i18n.t("screenCopy:localDataPanel.encryptedBackupDownloadedSaveItToYour"));
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") {
        setBackupState("canceled");
        setMessage(destination === "folder" ? folderText("canceled") : i18n.t("quickBackup:canceled"));
      } else {
        setBackupState("error");
        const permissionLost =
          caught instanceof DOMException && ["NotAllowedError", "SecurityError"].includes(caught.name);
        setError(
          caught instanceof BackupFolderError
            ? folderText(caught.code)
            : permissionLost && destination === "folder"
              ? folderText("permission")
              : caught instanceof Error
                ? caught.message
                : i18n.t("screenCopy:localDataPanel.backupFailed"),
        );
      }
    } finally {
      backupController.current = undefined;
      setBackupStage(undefined);
      setBusy(false);
    }
  }

  async function restoreBackup() {
    if (busy || !file) return;
    if (!window.confirm(i18n.t("screenCopy:localDataPanel.restoringReplacesAllLocalClassroomDataIn"))) return;
    try {
      setBusy(true);
      setBackupState("restoring");
      setError("");
      setMessage("");
      if (file.size > 100 * 1024 * 1024) throw new Error(i18n.t("quickBackup:tooLarge"));
      const backup = await decryptBackupInWorker(await file.text(), password);
      await onRestore(backup.snapshot, backup.sqliteImage, backup.schemaVersion);
      setBackupState("restored");
      setMessage(i18n.t("screenCopy:localDataPanel.backupRestoredCheckThatYourClassesAnd"));
      setFile(undefined);
    } catch (caught) {
      setBackupState("error");
      setError(
        caught instanceof Error
          ? caught.message
          : i18n.t("screenCopy:localDataPanel.restoreFailedExistingDataIsUnchanged"),
      );
    } finally {
      setBusy(false);
    }
  }

  async function requestPersistence() {
    if (busy || persistencePending || !status) return;
    setPersistenceError("");
    if (typeof navigator.storage?.persist !== "function") {
      setPersistenceMessage(copy("persistenceUnsupported"));
      return;
    }
    setPersistencePending(true);
    setPersistenceMessage(copy("persistencePending"));
    try {
      const granted = await requestPersistentStorage();
      onStorageStatusChange({ ...status, persistent: granted });
      setPersistenceMessage(copy(granted ? "persistenceGranted" : "persistenceNotGranted"));
    } catch {
      setPersistenceMessage("");
      setPersistenceError(copy("persistenceError"));
    } finally {
      setPersistencePending(false);
    }
  }

  return (
    <section
      className="local-data-layout"
      aria-label={t("localDataRegion")}
      aria-busy={busy}
      data-backup-state={backupState}
    >
      <ToolPageHeader title={i18n.t("screenCopy:localDataPanel.localData")} sessionStatus={sessionStatus} />
      {backupControl}
      <section className="paper-card">
        <h2>{i18n.t("screenCopy:localDataPanel.dataInThisBrowser")}</h2>
        <p>{copy("storageWarning")}</p>
        <dl>
          <div>
            <dt>OPFS</dt>
            <dd>
              {status?.opfsAvailable
                ? i18n.t("screenCopy:localDataPanel.available")
                : i18n.t("screenCopy:localDataPanel.checkingUnsupported")}
            </dd>
          </div>
          <div>
            <dt>{i18n.t("screenCopy:localDataPanel.persistentStorage")}</dt>
            <dd>
              {!status
                ? copy("persistenceChecking")
                : status.persistent
                  ? i18n.t("screenCopy:localDataPanel.granted")
                  : i18n.t("screenCopy:localDataPanel.notGranted")}
            </dd>
          </div>
          {status?.usageBytes !== undefined && (
            <div>
              <dt>{i18n.t("screenCopy:localDataPanel.siteStorageUsed")}</dt>
              <dd>{(status.usageBytes / 1024 / 1024).toFixed(1)} MB</dd>
            </div>
          )}
        </dl>
        {status && storageCapacityNotice(status) && (
          <p role="status" aria-label={t("storageCapacityStatus")}>
            {storageCapacityNotice(status)}
          </p>
        )}
        {!status?.persistent && (
          <>
            <button
              type="button"
              disabled={busy || persistencePending || !status}
              onClick={() => void requestPersistence()}
            >
              {copy("persistenceRequest")}
            </button>
            <p>{copy("persistenceExplanation")}</p>
          </>
        )}
        <p role="status" aria-label={copy("persistenceResult")} aria-atomic="true">
          {persistenceMessage}
        </p>
        {persistenceError && (
          <p className="form-error" role="alert" aria-label={copy("persistenceResult")}>
            {persistenceError}
          </p>
        )}
      </section>
      <section className="paper-card">
        <h2>{i18n.t("keyboard:backupDownloadAction")}</h2>
        <p>{yearText("backupScope")}</p>
        <label>
          {i18n.t("quickBackup:password")}
          <input
            type="password"
            autoComplete="new-password"
            disabled={busy}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        <button
          className="primary-action"
          aria-label={t("backupDownloadAction")}
          disabled={busy || password.length < 12}
          onClick={() => void downloadBackup()}
        >
          {busy ? i18n.t("screenCopy:localDataPanel.processing") : i18n.t("keyboard:backupDownloadAction")}
        </button>
        {supportsBackupDirectory() ? (
          <button type="button" disabled={busy || password.length < 12} onClick={() => void downloadBackup("folder")}>
            {folderText("chooseAction")}
          </button>
        ) : (
          <p>{folderText("unsupported")}</p>
        )}
        {backupStage && (
          <div>
            <p role="status" aria-label={t("backupProgressStatus")}>
              {backupState === "folder-writing"
                ? folderText("writing")
                : backupStage === "snapshot"
                  ? i18n.t("screenCopy:localDataPanel.readingDataAndCreatingAConsistentSnapshot")
                  : i18n.t("screenCopy:localDataPanel.encryptingBackup")}
            </p>
            <button type="button" onClick={() => backupController.current?.abort()}>
              {i18n.t("quickBackup:cancelBackup")}
            </button>
          </div>
        )}
        <small>{copy("passwordWarning")}</small>
      </section>
      <section className="paper-card">
        <h2>{i18n.t("screenCopy:localDataPanel.restoreFromEncryptedFile")}</h2>
        <p>{copy("restoreWarning")}</p>
        <label>
          {i18n.t("screenCopy:localDataPanel.chooseBackupFile")}
          <input
            type="file"
            disabled={busy}
            accept=".rollclassbackup,.json,application/json,application/octet-stream"
            onChange={(event) => setFile(event.target.files?.[0])}
          />
        </label>
        <button
          className="danger-button"
          aria-label={t("backupRestoreAction")}
          disabled={busy || !file || !password}
          onClick={() => void restoreBackup()}
        >
          {busy ? i18n.t("screenCopy:localDataPanel.processing") : i18n.t("keyboard:backupRestoreAction")}
        </button>
      </section>
      {!hideCloudImport && (
        <CloudImportPanel
          onPreviewCloudImport={onPreviewCloudImport}
          onImportCloudExport={onImportCloudExport}
          disabled={busy}
        />
      )}
      {message && (
        <p className="local-data-message" role="status" aria-label={t("localDataResult")}>
          {message}
        </p>
      )}
      {error && (
        <p className="form-error" role="alert" aria-label={t("localDataError")}>
          {error}
        </p>
      )}
    </section>
  );
}
